import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { Clase, Ramo, Linea } from '../../types';
import SearchableSelect from '../../components/SearchableSelect';

const ClaseForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoClase } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [formData, setFormData] = useState<Clase>({
    codigoClase: '',
    nombreClase: '',
    codigoRamo: '',
    codigoLinea: '',
    estadoClase: 'ACTIVO',
    codigoRamoNavigation: null,
    codigoLineaNavigation: null,
    articuloXlistaPrecios: [],
    clases: [],
    descuentos: [],
    stocks: []
  });

  const loadRamos = useCallback(async () => {
    try {
      const response = await catalogosService.getRamos(1, 1000);
      if (response && response.items) {
        setRamos(response.items);
      } else {
        setRamos([]);
        enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar ramos:', error);
      setRamos([]);
      enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadLineas = useCallback(async (codigoRamo: string) => {
    try {
      const response = await catalogosService.getLineas(1, 1000, '', codigoRamo);
      if (response && response.items) {
        setLineas(response.items);
      } else {
        setLineas([]);
        enqueueSnackbar('Error al cargar las líneas', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar líneas:', error);
      setLineas([]);
      enqueueSnackbar('Error al cargar las líneas', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadClase = useCallback(async () => {
    if (!codigoClase) return;
    try {
      setLoading(true);
      const clase = await catalogosService.getClase(codigoClase);
      setFormData(clase);
      if (clase.codigoRamo) {
        await loadLineas(clase.codigoRamo);
      }
    } catch (error) {
      console.error('Error al cargar la clase:', error);
      enqueueSnackbar('Error al cargar la clase', { variant: 'error' });
      navigate('/inventario/clases');
    } finally {
      setLoading(false);
    }
  }, [codigoClase, enqueueSnackbar, navigate, loadLineas]);

  useEffect(() => {
    loadRamos();
    if (codigoClase) {
      loadClase();
    }
  }, [codigoClase, loadClase, loadRamos]);

  useEffect(() => {
    if (formData.codigoRamo) {
      loadLineas(formData.codigoRamo);
    }
  }, [formData.codigoRamo, loadLineas]);

  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked ? 'ACTIVO' : 'INACTIVO'
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
        setSaving(true);
        
        // Creamos un objeto parcial de Clase con solo los campos necesarios
        const claseData = {
            codigoClase: formData.codigoClase.trim(),
            nombreClase: formData.nombreClase.trim(),
            codigoRamo: formData.codigoRamo ? formData.codigoRamo.trim() : '',
            codigoLinea: formData.codigoLinea ? formData.codigoLinea.trim() : '',
            estadoClase: formData.estadoClase,
            // Campos requeridos por el tipo pero que no afectan la creación
            codigoRamoNavigation: null,
            codigoLineaNavigation: null,
            articuloXlistaPrecios: [],
            clases: [],
            descuentos: [],
            stocks: []
        } as Clase;

        if (codigoClase) {
            await catalogosService.updateClase(codigoClase, claseData);
        } else {
            await catalogosService.createClase(claseData);
        }
        
        enqueueSnackbar('Clase guardada exitosamente', { variant: 'success' });
        navigate('/inventario/clases');
    } catch (error: any) {
        console.error('Error al guardar la clase:', error);
        const mensajeError = error.response?.data || 'Error al guardar la clase';
        enqueueSnackbar(typeof mensajeError === 'string' ? mensajeError : 'Error al guardar la clase', { 
            variant: 'error' 
        });
    } finally {
        setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/inventario/clases')}
              sx={{ textTransform: 'none' }}
            >
              Clases
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              {codigoClase ? 'Editar Clase' : 'Nueva Clase'}
            </Typography>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="codigoClase"
                  label="Código"
                  value={formData.codigoClase}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  disabled={!!codigoClase}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="nombreClase"
                  label="Nombre"
                  value={formData.nombreClase}
                  onChange={handleTextFieldChange}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchableSelect
                  name="codigoRamo"
                  label="Ramo"
                  allowEmpty
                  emptyOptionLabel="Seleccione un ramo"
                  value={formData.codigoRamo || null}
                  onChange={(v) => {
                    const value = v ?? '';
                    setFormData(prev => ({
                      ...prev,
                      codigoRamo: value,
                      codigoLinea: '',
                      codigoLineaNavigation: null,
                    }));
                    if (value) {
                      loadLineas(value);
                    } else {
                      setLineas([]);
                    }
                  }}
                  options={ramos.map((ramo) => ({
                    value: ramo.codigoRamo,
                    label: ramo.nombreRamo,
                  }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SearchableSelect
                  name="codigoLinea"
                  label="Línea"
                  allowEmpty
                  emptyOptionLabel="Seleccione una línea"
                  disabled={!formData.codigoRamo}
                  value={formData.codigoLinea || null}
                  onChange={(v) => {
                    const value = v ?? '';
                    setFormData(prev => ({
                      ...prev,
                      codigoLinea: value,
                      codigoLineaNavigation: lineas.find(l => l.codigoLinea === value) || null,
                    }));
                  }}
                  options={lineas.map((linea) => ({
                    value: linea.codigoLinea,
                    label: linea.nombreLinea,
                  }))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.estadoClase === 'ACTIVO'}
                      onChange={handleSwitchChange}
                      name="estadoClase"
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21, 128, 61, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/clases')}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClaseForm; 